import { useState, useRef, useEffect } from "react";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";
import { requestOtp, requestPasswordReset, verifyOtp } from "../../api/authApi";
import { Navigate, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useAuthStore } from "../../store/auth.store";
import ToastContainer from "../../components/toastContainer";
import Toast from "../../components/toast";
import { getUserTypePermissions } from "../../api/permissionsApi";

const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [otpToken, setOtpToken] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [showOTP, setShowOTP] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const [toast, setToast] = useState(null);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  // Countdown
  const [timer, setTimer] = useState(30);

  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const navigate = useNavigate();

  const login = useAuthStore((state) => state.login);

  const setPermissions = useAuthStore((s) => s.setPermissions);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (showOTP && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showOTP, timer]);

  // Clear error on typing
  const clearErrors = () => {
    setError("");
    setOtpError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearErrors();

    if (!email || !password) {
      setError("Please fill in all fields.");
      return;
    }

    setLoading(true);

    try {
      const response = await requestOtp(email, password);
      setOtpToken(response.otp_token);
      setShowOTP(true);
      setTimer(30);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "") // only digits
      .slice(0, 6); // max 6 digits

    if (!pastedData) return;

    const newOtp = [...otp];

    pastedData.split("").forEach((digit, index) => {
      newOtp[index] = digit;
    });

    setOtp(newOtp);

    // Focus last filled input
    const lastIndex = pastedData.length - 1;
    if (inputRefs.current[lastIndex]) {
      inputRefs.current[lastIndex].focus();
    }
  };

  const handleOtpChange = (index, value) => {
    clearErrors();

    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleConfirmOtp = async () => {
    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      setOtpError("Please enter the 6-digit code.");
      return;
    }

    setOtpLoading(true);

    try {
      const res = await verifyOtp(email, otpCode, otpToken);

      login({
        access: res.access,
        refresh: res.refresh,
        user: res.user,
      });

      const permissions = await getUserTypePermissions(res.user.type.id);

      setPermissions(permissions);

      navigate("/dashboard/users", { replace: true });
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Invalid OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearErrors();

    if (!resetEmail) {
      setError("Please enter your email address.");
      return;
    }

    setResetLoading(true);

    try {
      // TODO: call forgot password API
      await requestPasswordReset(resetEmail);

      setToast({
        type: "success",
        message: "Password reset link sent to your email",
      });

      setShowForgotPassword(false);
      setResetEmail("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send reset link.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await requestOtp(email, password);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0].focus();
      setTimer(30);
      setOtpError("");
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Failed to resend OTP.");
    }
  };

  // const token = useAuthStore((state) => state.token);

  // if (token) {
  //   return <Navigate to="/users" replace />;
  // }

  if (isAuthenticated) {
    return <Navigate to="/dashboard/promoters" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center  bg-slate-950 px-4">
      <div className="w-full max-w-md bg-slate-900/60 border border-white/5 rounded-2xl p-8 shadow-xl">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div>
            <img src={logo} alt="Logo" className="w-50 h-20 object-contain" />
          </div>

          <div className="text-xs text-slate-400 uppercase tracking-[0.16em] mt-3">
            Afrodise Artist Dashboard
          </div>

          <p className="text-sm font-semibold text-white mt-1">
            {showOTP ? "Enter the verification code" : "Sign in to Continue"}
          </p>
        </div>

        {/* LOGIN FORM */}
        {!showOTP && !showForgotPassword && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-300 text-sm mb-1 text-left">
                Email:
              </label>
              <input
                type="email"
                className="w-full bg-[#131C31] border border-white/10 text-gray-200 rounded-lg py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-pink-500/50"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearErrors();
                }}
              />
            </div>

            <div className="relative">
              <label className="block text-gray-300 text-sm mb-1 text-left">
                Password:
              </label>

              <input
                type={showPassword ? "text" : "password"}
                className="w-full bg-[#131C31] border border-white/10 text-gray-200 rounded-lg py-2.5 px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-pink-500/50"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearErrors();
                }}
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-10 text-gray-400"
              >
                {showPassword ? (
                  <EyeIcon className="h-5 w-5" />
                ) : (
                  <EyeOffIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  clearErrors();
                  setShowForgotPassword(true);
                }}
                className="text-sm text-pink-500 hover:text-pink-400"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-lg py-2.5 font-medium text-white
                      ${
                        loading
                          ? "bg-pink-400 cursor-not-allowed"
                          : "bg-pink-600 hover:bg-pink-700"
                      }
                    `}
            >
              {loading ? "Sending OTP..." : "GET OTP"}
            </button>
          </form>
        )}

        {showForgotPassword && (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div>
              <label className="block text-gray-300 text-sm mb-1 text-left">
                Email:
              </label>
              <input
                type="email"
                className="w-full bg-[#131C31] border border-white/10 text-gray-200 rounded-lg py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-pink-500/50"
                placeholder="you@example.com"
                value={resetEmail}
                onChange={(e) => {
                  setResetEmail(e.target.value);
                  clearErrors();
                }}
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={resetLoading}
              className={`w-full rounded-lg py-2.5 font-medium text-white
                ${
                  resetLoading
                    ? "bg-pink-400 cursor-not-allowed"
                    : "bg-pink-600 hover:bg-pink-700"
                }
              `}
            >
              {resetLoading ? "Sending..." : "Send reset link"}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmail("");
              }}
              className="w-full text-sm text-slate-400 hover:text-white"
            >
              ← Back to login
            </button>
          </form>
        )}

        {/* OTP FORM */}
        {showOTP && (
          <div className="space-y-6 mt-4">
            {/* OTP Boxes */}
            <div className="flex justify-between gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  className="w-12 h-12 text-center text-xl text-white bg-[#131C31] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/50"
                  value={digit}
                  ref={(el) => (inputRefs.current[index] = el)}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                />
              ))}
            </div>

            {/* OTP Error */}
            {otpError && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
                {otpError}
              </div>
            )}

            {/* Countdown + Resend */}
            <div className="flex justify-center text-gray-400 text-sm">
              {timer > 0 ? (
                <span>Resend code in {timer}s</span>
              ) : (
                <button
                  disabled={timer > 0}
                  onClick={handleResend}
                  className={`${
                    timer > 0
                      ? "text-gray-500 cursor-not-allowed"
                      : "text-pink-500 hover:text-pink-400"
                  }`}
                >
                  Resend Code
                </button>
              )}
            </div>

            {/* Confirm Button */}
            <button
              onClick={handleConfirmOtp}
              disabled={otpLoading}
              className={`w-full rounded-lg py-2.5 font-medium text-white
                ${
                  otpLoading
                    ? "bg-pink-400 cursor-not-allowed"
                    : "bg-pink-600 hover:bg-pink-700"
                }
              `}
            >
              {otpLoading ? "Verifying..." : "Confirm & Sign In"}
            </button>
          </div>
        )}
      </div>

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default LoginScreen;
